import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { FeatureFlag } from 'js/src/client';
import { ApiService, Urls } from 'js/src/client';
import { PrimaryButton } from 'js/src/components/common/ui/Button';
import Modal from 'js/src/components/common/ui/modal';
import Spinner from 'js/src/components/common/ui/Spinner';
import SwitchToggle from 'js/src/components/common/ui/SwitchToggle';
import { hideModal, isFeatureFlagsManagementModalShowing } from 'js/src/data/modals';
import { useAppDispatch } from 'js/src/libs/RootState';
import { getServerData } from 'js/src/libs/server-data';

/**
 * Mirror the toggle onto the server blob that `hasFeatureEnabled` reads.
 *
 * Flags are read straight off `window.SERVER` all over the app, so without this the
 * page would keep serving the flags it was rendered with until a reload. Mutating the
 * array in place means anything that re-renders (or re-reads the flag) afterwards picks
 * up the new value. Flags that are only read once — at store creation, during Sentry
 * init, or when building the Luminary config — still need a reload to take effect.
 */
const syncFlagOntoServerData = (flagName: string, isEnrolled: boolean): void => {
    const user = getServerData()?.USER;
    if (!user?.feature_flags) {
        return;
    }
    const index = user.feature_flags.indexOf(flagName);
    if (isEnrolled && index === -1) {
        user.feature_flags.push(flagName);
    } else if (!isEnrolled && index !== -1) {
        user.feature_flags.splice(index, 1);
    }
};

const FeatureFlagsModal: React.FC = () => {
    const isShowing = useSelector(isFeatureFlagsManagementModalShowing);
    const dispatch = useAppDispatch();
    const [flags, setFlags] = useState<FeatureFlag[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [togglingFlags, setTogglingFlags] = useState<Set<string>>(new Set());

    const fetchFlags = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ApiService.flagListAllList();
            setFlags(response.data);
        } catch (e) {
            setError('Failed to load feature flags');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isShowing) {
            void fetchFlags();
        }
    }, [isShowing, fetchFlags]);

    const handleToggle = useCallback(async (flagName: string, isCurrentlyEnrolled: boolean) => {
        setTogglingFlags((prev) => new Set(prev).add(flagName));
        try {
            if (isCurrentlyEnrolled) {
                await ApiService.flagWithdrawRetrieve({ path: { id: flagName } });
            } else {
                await ApiService.flagEnrollRetrieve({ path: { id: flagName } });
            }
            syncFlagOntoServerData(flagName, !isCurrentlyEnrolled);
            setFlags((prev) =>
                prev.map((flag) =>
                    flag.name === flagName ? { ...flag, is_enrolled: !isCurrentlyEnrolled } : flag,
                ),
            );
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to toggle feature flag:', e);
        } finally {
            setTogglingFlags((prev) => {
                const next = new Set(prev);
                next.delete(flagName);
                return next;
            });
        }
    }, []);

    const onClose = useCallback(() => {
        dispatch(hideModal());
    }, [dispatch]);

    const handleSwitchChange = useCallback(
        (flagName: string, isCurrentlyEnrolled: boolean) => () => {
            void handleToggle(flagName, isCurrentlyEnrolled);
        },
        [handleToggle],
    );

    const footer = (
        <div className="d-flex justify-content-between align-items-center w-100">
            <p className="font-l5 font-l5--extra-small mb-l5-0">
                Available flags can be managed{' '}
                <a href={Urls.FEATURE_FLAGS_CHANGELIST} target="_blank">
                    here
                </a>
            </p>
            <PrimaryButton text="Done" onClick={onClose} />
        </div>
    );

    return (
        <Modal
            showing={isShowing}
            title="Feature flags [staff only]"
            onHidden={onClose}
            footer={footer}
        >
            <div>
                {loading && (
                    <div className="d-flex justify-content-center py-l5-32">
                        <Spinner />
                    </div>
                )}
                {error && <div className="text-danger">{error}</div>}
                {!loading && !error && flags.length === 0 && (
                    <div className="text-muted">No active feature flags found.</div>
                )}
                {!loading && !error && flags.length > 0 && (
                    <div className="d-flex flex-column gap-3">
                        {flags.map((flag) => (
                            <div key={flag.name} className="d-flex align-items-center gap-3">
                                <SwitchToggle
                                    checked={flag.is_enrolled}
                                    disabled={togglingFlags.has(flag.name)}
                                    onChange={handleSwitchChange(flag.name, flag.is_enrolled)}
                                />
                                <span className="font-monospace">{flag.name}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default FeatureFlagsModal;
